// ===========================================
// SMS Character Count Utility
// ===========================================
// Calculates SMS segment counts based on GSM-7 vs UCS-2 encoding

// GSM-7 basic character set (128 chars)
const GSM7_BASIC = new Set([
  '@', '£', '$', '¥', 'è', 'é', 'ù', 'ì', 'ò', 'Ç', '\n', 'Ø', 'ø', '\r', 'Å', 'å',
  'Δ', '_', 'Φ', 'Γ', 'Λ', 'Ω', 'Π', 'Ψ', 'Σ', 'Θ', 'Ξ', '\x1b', 'Æ', 'æ', 'ß', 'É',
  ' ', '!', '"', '#', '¤', '%', '&', '\'', '(', ')', '*', '+', ',', '-', '.', '/',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':', ';', '<', '=', '>', '?',
  '¡', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O',
  'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ä', 'Ö', 'Ñ', 'Ü', '§',
  '¿', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o',
  'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'ä', 'ö', 'ñ', 'ü', 'à',
]);

// GSM-7 extended character set (each counts as 2 chars due to escape prefix)
const GSM7_EXTENDED = new Set([
  '\f', '^', '{', '}', '\\', '[', '~', ']', '|', '€',
]);

export interface SmsCharacterCountResult {
  /** Total characters in the message */
  characterCount: number;
  /** Number of SMS segments this will use */
  segmentCount: number;
  /** Characters remaining before next segment */
  remainingInSegment: number;
  /** Whether encoding is GSM-7 (true) or UCS-2 (false) */
  isGsm7: boolean;
  /** Max chars per single SMS message */
  singleSmsLimit: number;
  /** Max chars per segment in a multi-part SMS */
  multiSmsSegmentLimit: number;
}

/**
 * Calculates the SMS character count, segment count, and encoding type.
 * GSM-7: 160 chars per single SMS, 153 per segment in multi-part.
 * UCS-2: 70 chars per single SMS, 67 per segment in multi-part.
 */
export function calculateSmsCharacterCount(text: string): SmsCharacterCountResult {
  if (!text) {
    return {
      characterCount: 0,
      segmentCount: 1,
      remainingInSegment: 160,
      isGsm7: true,
      singleSmsLimit: 160,
      multiSmsSegmentLimit: 153,
    };
  }

  // Check if all characters are GSM-7 compatible
  let isGsm7 = true;
  let gsm7Length = 0;

  for (const char of text) {
    if (GSM7_BASIC.has(char)) {
      gsm7Length += 1;
    } else if (GSM7_EXTENDED.has(char)) {
      // Extended chars take 2 characters (escape + char)
      gsm7Length += 2;
    } else {
      // Non-GSM-7 character — must use UCS-2
      isGsm7 = false;
      break;
    }
  }

  if (isGsm7) {
    const singleSmsLimit = 160;
    const multiSmsSegmentLimit = 153;

    if (gsm7Length <= singleSmsLimit) {
      return {
        characterCount: gsm7Length,
        segmentCount: 1,
        remainingInSegment: singleSmsLimit - gsm7Length,
        isGsm7: true,
        singleSmsLimit,
        multiSmsSegmentLimit,
      };
    }

    const segmentCount = Math.ceil(gsm7Length / multiSmsSegmentLimit);
    const usedInLastSegment = gsm7Length - (segmentCount - 1) * multiSmsSegmentLimit;
    const remainingInSegment = multiSmsSegmentLimit - usedInLastSegment;

    return {
      characterCount: gsm7Length,
      segmentCount,
      remainingInSegment,
      isGsm7: true,
      singleSmsLimit,
      multiSmsSegmentLimit,
    };
  }

  // UCS-2 encoding — count Unicode code points
  const ucs2Length = [...text].length;
  const singleSmsLimit = 70;
  const multiSmsSegmentLimit = 67;

  if (ucs2Length <= singleSmsLimit) {
    return {
      characterCount: ucs2Length,
      segmentCount: 1,
      remainingInSegment: singleSmsLimit - ucs2Length,
      isGsm7: false,
      singleSmsLimit,
      multiSmsSegmentLimit,
    };
  }

  const segmentCount = Math.ceil(ucs2Length / multiSmsSegmentLimit);
  const usedInLastSegment = ucs2Length - (segmentCount - 1) * multiSmsSegmentLimit;
  const remainingInSegment = multiSmsSegmentLimit - usedInLastSegment;

  return {
    characterCount: ucs2Length,
    segmentCount,
    remainingInSegment,
    isGsm7: false,
    singleSmsLimit,
    multiSmsSegmentLimit,
  };
}
